import React, { useEffect, useState } from "react";
import { TrendingUp, ShoppingCart, Users, AlertTriangle, Euro, Package } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import api, { eur } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { STATUS_LABELS } from "@/pages/OrderTracking";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card-surface rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
        <Icon className={`w-5 h-5 ${accent ? "text-[#FF2E4C]" : "text-zinc-600"}`} />
      </div>
      <p className="font-display text-2xl sm:text-3xl font-black">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => {}); }, []);
  if (!a) return <div className="p-6"><div className="w-8 h-8 border-2 border-[#FF2E4C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Dashboard" subtitle="ΕΠΙΣΚΟΠΗΣΗ" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={Euro} label="Πωλήσεις Σήμερα" value={eur(a.today_sales)} accent />
        <Stat icon={TrendingUp} label="Εβδομάδα" value={eur(a.weekly_sales)} />
        <Stat icon={TrendingUp} label="Μήνας" value={eur(a.monthly_sales)} />
        <Stat icon={Euro} label="Συνολικές Πωλήσεις" value={eur(a.total_sales)} accent />
        <Stat icon={ShoppingCart} label="Παραγγελίες" value={a.order_count} />
        <Stat icon={Euro} label="Μ.Ο. Παραγγελίας" value={eur(a.aov)} />
        <Stat icon={Users} label="Πελάτες" value={a.customers} />
        <Stat icon={Package} label="Εκκρεμείς" value={a.pending_orders} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface rounded-lg p-6">
          <h3 className="font-display font-bold uppercase text-sm mb-6">Πωλήσεις 7 ημερών</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={a.revenue_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="date" stroke="#71717A" fontSize={11} />
              <YAxis stroke="#71717A" fontSize={11} />
              <Tooltip contentStyle={{ background: "#18181C", border: "1px solid #27272A", borderRadius: 8 }} formatter={(v) => eur(v)} cursor={{ fill: "rgba(255,46,76,0.1)" }} />
              <Bar dataKey="revenue" fill="#FF2E4C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-surface rounded-lg p-6">
          <h3 className="font-display font-bold uppercase text-sm mb-4">Best Sellers</h3>
          <div className="space-y-3">
            {a.best_sellers.map((p, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="font-mono text-zinc-600 text-sm w-4">{i + 1}</span>
                {p.image && <img src={p.image} alt="" className="w-10 h-12 object-cover rounded" />}
                <div className="flex-1 min-w-0"><p className="text-xs font-semibold truncate">{p.name}</p><p className="text-[10px] text-zinc-500">{p.sold} πωλήσεις</p></div>
              </div>
            ))}
            {a.best_sellers.length === 0 && <p className="text-zinc-500 text-sm">Δεν υπάρχουν δεδομένα.</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <div className="card-surface rounded-lg p-6">
          <h3 className="font-display font-bold uppercase text-sm mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#FF2E4C]" /> Χαμηλό Απόθεμα</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {a.low_stock.length === 0 ? <p className="text-zinc-500 text-sm">Όλα καλά με το απόθεμα!</p> :
              a.low_stock.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-zinc-800 pb-2">
                  <span className="truncate">{s.name} <span className="text-zinc-500 font-mono text-xs">({s.color}/{s.size})</span></span>
                  <span className={`font-mono font-bold ${s.stock === 0 ? "text-red-500" : "text-yellow-500"}`}>{s.stock === 0 ? "SOLD OUT" : s.stock}</span>
                </div>
              ))}
          </div>
        </div>
        <div className="card-surface rounded-lg p-6">
          <h3 className="font-display font-bold uppercase text-sm mb-4">Παραγγελίες ανά κατάσταση</h3>
          <div className="space-y-2">
            {Object.entries(a.status_counts).map(([s, c]) => (
              <div key={s} className="flex items-center justify-between text-sm border-b border-zinc-800 pb-2">
                <span>{STATUS_LABELS[s] || s}</span><span className="font-mono font-bold">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
